import { api } from './api'

/* ------------------------------------------------------------------
   ثبت Service Worker و اشتراک Push

   ==================================================================
   ⚠️ همهٔ توابع اینجا **بی‌خطر شکست می‌خورند**.

   🔴 مرورگرِ بدون پشتیبانی، اجازهٔ ردشده، یا سرورِ بدون کلید VAPID —
   هیچ‌کدام نباید ورود کاربر یا بارگذاری صفحه را بشکنند. اعلان
   قابلیتی جانبی است؛ نبودش آزاردهنده است، ولی خطا دادنش به‌خاطرش
   بدتر.
   ==================================================================
   ------------------------------------------------------------------ */

export type PushState =
  | 'unsupported'
  | 'disabled_on_server'
  | 'denied'
  /** پنجرهٔ اجازه بی‌پاسخ بسته شد — یا مرورگر اصلاً نشانش نداد (کروم پس از چند بار بستن، بی‌صدا). */
  | 'dismissed'
  | 'subscribed'
  | 'unsubscribed'

/** کلید VAPID از base64url به `Uint8Array` — قالبی که مرورگر می‌خواهد. */
function urlBase64ToUint8Array(base64: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const normalised = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(normalised)

  /*
    ⚠️ `ArrayBuffer` برگردانده می‌شود نه `Uint8Array`.

    🔴 امضای `applicationServerKey` در TypeScript جدید `BufferSource`
    است و `Uint8Array<ArrayBufferLike>` با آن یکی نیست. `.buffer`
    همان بایت‌هاست، فقط با نوعی که مرورگر و تایپ هر دو می‌پذیرند.
  */
  const bytes = Uint8Array.from([...raw].map((c) => c.charCodeAt(0)))

  return bytes.buffer as ArrayBuffer
}

export function isPushSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window &&
    /*
      🔴 Push فقط در بستر امن کار می‌کند.

      ⚠️ `localhost` استثناست تا توسعه ممکن باشد؛ در شبکهٔ داخلی با
      HTTP ساده، مرورگر بی‌صدا رد می‌کند و اینجا صریح می‌گوییم.
    */
    (window.isSecureContext || location.hostname === 'localhost')
  )
}

/** ثبت Service Worker — بی‌صدا در صورت شکست. */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null

  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch {
    return null
  }
}

/** وعده‌ای که پس از `ms` میلی‌ثانیه با خطا تمام می‌شود، اگر زودتر برنگشته باشد. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      window.setTimeout(() => reject(new Error('push timed out')), ms)
    }),
  ])
}

/**
 * Service Worker ِ ثبت‌شده و **فعال**.
 *
 * 🔴 `pushManager.subscribe` بی کارگرِ فعال رد می‌شود. در توسعه کارگر تا
 * نخستین فعال‌سازی ثبت نشده و در تولید ممکن است هنوز در حال نصب باشد؛
 * پس نخستین کلیک خطا می‌داد و کاربر باید دوباره می‌زد (۱۴۰۵/۰۸/۰۳: «با یه
 * بار فعال کردن باید فعال شه»). `ready` همان لحظهٔ فعال شدن برمی‌گردد.
 */
async function activeRegistration(): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration()

  if (!existing && !(await registerServiceWorker())) return null

  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => window.setTimeout(() => resolve(null), 10_000)),
  ])
}

type PushKey = { public_key: string | null; enabled: boolean }

let keyRequest: Promise<PushKey> | null = null

/**
 * کلیدِ عمومیِ سرور — یک بار در هر بارگذاری.
 *
 * ⚠️ دروازهٔ اعلان پیش از نشان دادنِ خودش همین را می‌پرسد، پس هنگامِ کلیک
 * پاسخ آماده است و `requestPermission` بی معطلیِ شبکه درست پس از کلیک صدا
 * زده می‌شود — فایرفاکس اجازه را فقط در همان لحظه‌های پس از کلیک می‌پرسد.
 */
export function serverPushKey(): Promise<PushKey> {
  keyRequest ??= api
    .get<{ data: PushKey }>('/me/push/key')
    .then(({ data }) => data.data)
    .catch((error: unknown) => {
      keyRequest = null
      throw error
    })

  return keyRequest
}

/** وضعیت فعلی اشتراک. */
export async function pushState(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported'

  if (Notification.permission === 'denied') return 'denied'

  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()

  return subscription ? 'subscribed' : 'unsubscribed'
}

/**
 * فعال‌سازی اعلان روی این دستگاه.
 *
 * ⚠️ ترتیب عمدی است: اول کلید سرور، بعد اجازهٔ کاربر.
 *
 * 🔴 اگر برعکس بود، از کاربر اجازه می‌گرفتیم و بعد می‌فهمیدیم سرور
 * اصلاً Push ندارد — و اجازه‌ای که رد شود، دیگر به‌سادگی پس گرفته
 * نمی‌شود.
 */
export async function enablePush(): Promise<PushState> {
  if (!isPushSupported()) return 'unsupported'

  const key = await serverPushKey()

  if (!key.enabled || !key.public_key) return 'disabled_on_server'

  const permission = await Notification.requestPermission()

  if (permission === 'denied') return 'denied'

  if (permission !== 'granted') return 'dismissed'

  const registration = await activeRegistration()

  /* ⚠️ کارگر در ده ثانیه فعال نشد — خطا، تا «دوباره امتحان کن» دیده شود. */
  if (!registration) throw new Error('service worker did not activate')

  /*
    ⚠️ اگر قبلاً اشتراکی هست، همان دوباره به سرور فرستاده می‌شود.

    🔴 سقفِ زمان: وقتی سرویسِ Push ِ مرورگر (FCM برای کروم) در دسترس نیست،
    `subscribe` گاهی هرگز برنمی‌گردد و دکمه تا ابد می‌چرخید — مودالِ
    بی‌راه‌فرار هم پنل را قفل می‌کرد. پس از سقف، خطا: «دوباره امتحان کن».
  */
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await withTimeout(registration.pushManager.subscribe({
      /*
        🔴 `userVisibleOnly` اجباری است.

        ⚠️ مرورگر Push خاموش (بدون نمایش اعلان) را نمی‌پذیرد — و درست
        هم می‌کند: پیامِ نامرئی یعنی ردیابی.
      */
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key.public_key),
    }), 15_000))

  const json = subscription.toJSON() as {
    endpoint?: string
    keys?: { p256dh?: string; auth?: string }
  }

  await api.post('/me/push/subscribe', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    device_label: navigator.platform || undefined,
  })

  return 'subscribed'
}

/** قطع اعلان روی این دستگاه. */
export async function disablePush(): Promise<PushState> {
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()

  if (!subscription) return 'unsubscribed'

  const endpoint = subscription.endpoint

  await subscription.unsubscribe()

  /*
    ⚠️ خطای سرور نباید قطع سمت مرورگر را برگرداند.

    🔴 کاربر گفته «نمی‌خواهم»؛ اگر به‌خاطر خطای شبکه دوباره مشترک
    می‌ماند، خواستهٔ صریحش نادیده گرفته شده بود. ردیفِ یتیم در سرور
    با نخستین خطای ۴۱۰ خودش پاک می‌شود.
  */
  try {
    await api.post('/me/push/unsubscribe', { endpoint })
  } catch {
    /* عمداً خاموش */
  }

  return 'unsubscribed'
}

/* ------------------------------------------------------------------
   «متوجه شدم» ِ دروازهٔ اعلان — تا ورودِ بعدی

   🔴 ۱۴۰۵/۰۸/۰۳ صاحبِ پروژه: «با یه بار فعال کردن باید فعال شه، یا اگه
   نمی‌شه نباید هی بیاد.» این انتخاب پیش‌تر در حافظهٔ کامپوننت بود و با
   هر تازه‌کردنِ صفحه دوباره همان مودال روی صفحه می‌نشست.

   ⚠️ در `localStorage`، و ورود (`AuthProvider`) پاکش می‌کند: با ورودِ بعدی
   دوباره پرسیده می‌شود — همان قصدِ اولِ دروازه. خطای ذخیره (حالتِ خصوصی)
   فقط یعنی رفتارِ قبلی.
   ------------------------------------------------------------------ */
const PROMPT_KEY = 'pf:push-prompt-dismissed'

export function dismissedPushPrompt(): string | null {
  try {
    return window.localStorage.getItem(PROMPT_KEY)
  } catch {
    return null
  }
}

export function dismissPushPrompt(state: string): void {
  try {
    window.localStorage.setItem(PROMPT_KEY, state)
  } catch {
    /* عمداً خاموش */
  }
}

export function forgetPushPromptDismissal(): void {
  try {
    window.localStorage.removeItem(PROMPT_KEY)
  } catch {
    /* عمداً خاموش */
  }
}
